import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  CopilotKnowledgeFileReadinessError,
  getCopilotKnowledgeFileReadinessInputSchema,
  type CopilotKnowledgeFileReadinessApi,
} from "../api/copilot-knowledge-file-readiness-api.js";

export const getCopilotKnowledgeFileReadinessTool: Tool = {
  name: "get_copilot_knowledge_file_readiness",
  description:
    "EXPERIMENTAL read-only native indexing-status diagnostic for one exact uploaded file in the current Dataverse environment. Verifies the bot and its type-14 component/file metadata, then sends one fixed status-query POST. Existing same-account silent org auth only; 15-second caller deadline, bounded responses, no retries or fallback. Returns closed Completed/PartiallyCompleted/InProgress/Error status or unavailable/unknown, never file content or raw errors. A reported Completed status is not current-file-hash ingestion or agent-answer proof. HTTP404 is unavailable, not indexing-in-progress proof; entity-not-in-index is reported only for the exact observed service diagnostic. No ingestion trigger, provisioning, upload, publication or configuration change. Requires a supported preverified same-account org provider; auth work itself cannot be canceled, but late completion cannot start HTTP.",
  inputSchema: {
    ...z.toJSONSchema(getCopilotKnowledgeFileReadinessInputSchema, {
      target: "draft-7",
      io: "input",
    }),
    type: "object",
  } as Tool["inputSchema"],
  annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true },
};

export async function handleGetCopilotKnowledgeFileReadiness(
  context: { knowledgeFileReadinessApi: CopilotKnowledgeFileReadinessApi },
  args: unknown,
) {
  const parsed = getCopilotKnowledgeFileReadinessInputSchema.safeParse(args);
  if (!parsed.success) throw new CopilotKnowledgeFileReadinessError("input");
  const result = await context.knowledgeFileReadinessApi.getReadiness(parsed.data);
  return {
    text:
      `File indexing status: ${result.readiness} (HTTP ${result.httpStatus}). ` +
      (result.reason === "entity_not_in_index"
        ? "The status service reported that botcomponent is not in the index. "
        : "") +
      result.notice,
    structured: { copilotKnowledgeFileReadiness: result },
    ...(result.querySucceeded ? {} : { isError: true }),
  };
}
